import React, { useState, useEffect } from 'react';
import TextInputDuoList from '../TextInputDuoList';
import { useTranslation } from '../../context/TranslationContext';
import debug from '@commons-shared/debug';
import { config } from '../../config/environment';


const FormatsTab = () => {
  const { t } = useTranslation();
  
  // Initialiser les états avec les données du localStorage si disponibles
  const [customFormats, setCustomFormats] = useState(() => {
    const savedFormats = localStorage.getItem('bel_formats');
    if (savedFormats) {
      try {
        const parsed = JSON.parse(savedFormats);
        return parsed.customFormats || [];
      } catch (error) {
        debug.error(config, 'Erreur lors du chargement initial des formats depuis localStorage', error);
        return [];
      }
    }
    return [];
  });
  
  const [bbcodeImageFormat, setBbcodeImageFormat] = useState(() => {
    const savedFormats = localStorage.getItem('bel_formats');
    if (savedFormats) {
      try {
        const parsed = JSON.parse(savedFormats);
        return parsed.bbcodeImageFormat || '';
      } catch (error) {
        debug.error(config, 'Erreur lors du chargement initial du format image depuis localStorage', error);
        return '';
      }
    }
    return '';
  });
  
  const [isLoading, setIsLoading] = useState(false);

  // Fonction pour charger les données depuis localStorage
  const loadFromLocalStorage = () => {
    setIsLoading(true);
    const savedFormats = localStorage.getItem('bel_formats');
    if (savedFormats) {
      try {
        const parsed = JSON.parse(savedFormats);
        if (parsed.customFormats) {
          debug.log(config, 'Loading customFormats from localStorage:', parsed.customFormats);
          setCustomFormats(parsed.customFormats);
        }
        if (parsed.bbcodeImageFormat) {
          setBbcodeImageFormat(parsed.bbcodeImageFormat);
        }
      } catch (error) {
        debug.error(config, 'Erreur lors du chargement des formats depuis localStorage', error);
      }
    }
    setIsLoading(false);
  };

  // Charger les données au montage du composant
  useEffect(() => {
    loadFromLocalStorage();
  }, []);

  // Écouter les changements du localStorage (pour l'import de configuration)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'bel_formats') {
        loadFromLocalStorage();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Fonction pour sauvegarder dans localStorage
  const saveToLocalStorage = (customFormatsData, imageFormatData) => {
    const formatsData = {
      customFormats: customFormatsData,
      bbcodeImageFormat: imageFormatData,
      lastUpdated: new Date().toISOString()
    };
    
    try {
      localStorage.setItem('bel_formats', JSON.stringify(formatsData));
      debug.success(config, 'Formats sauvegardés dans localStorage', formatsData);
    } catch (error) {
      debug.error(config, 'Erreur lors de la sauvegarde dans localStorage', error);
    }
  };

  const handleCustomFormatsChange = (data) => {
    if (!isLoading) {
      setCustomFormats(data);
      saveToLocalStorage(data, bbcodeImageFormat);
      debug.log(config, 'Custom formats changed', data);
    }
  };

  const handleImageFormatChange = (e) => {
    if (!isLoading) {
      const newValue = e.target.value;
      setBbcodeImageFormat(newValue);
      saveToLocalStorage(customFormats, newValue);
      debug.log(config, 'BBCode image format changed', newValue);
    }
  };

  return (
    <div className="wa-text-input-duo-list wa-text-input-duo-list--compact">
      {/* Texte explicatif */}
      <div className="wa-text-input-duo-list--bordered" style={{
        backgroundColor: 'var(--color-primary-light)',
        borderColor: 'var(--color-primary)',
        padding: 'var(--spacing-lg)',
        borderRadius: 'var(--radius-md)'
      }}>
        <p style={{ color: 'var(--color-primary)', fontSize: '0.875rem', margin: 0 }}>
          {t('HTML_TO_BBCODE_FORMAT_OPTIONS', 'HTML to BBCode format options')}
        </p>
      </div>

      {/* TextInputDuoList pour les formats personnalisés */}
      <TextInputDuoList
        key={`formats-${customFormats.length}-${JSON.stringify(customFormats).substring(0, 50)}`}
        title="Manuscript Custom formats"
        initialCount={5}
        allowAdd={false}
        numbering={true}
        label1=">BBCode"
        label2=">HTML"
        placeholder1="[aloud]$1[/aloud]"
        placeholder2="\\[aloud\\](.+)\\[/aloud\\]"
        radius="md"
        bordered={true}
        initialData={customFormats}
        onChange={handleCustomFormatsChange}
      />

      {/* Champ pour le format BBCode des images */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-sm)' }}>
        <label style={{
          fontSize: '0.875rem',
          fontWeight: '500',
          color: 'var(--text-primary)'
        }}>
          {t('BBCODE_FORMAT_FOR_IMAGE_PARTS', 'BBCode format for the image of image parts')}
        </label>
        <input
          type="text"
          value={bbcodeImageFormat}
          onChange={handleImageFormatChange}
          style={{
            padding: 'var(--spacing-sm) var(--spacing-md)',
            border: '1px solid var(--border-medium)',
            borderRadius: 'var(--radius-md)',
            backgroundColor: 'var(--bg-primary)',
            color: 'var(--text-primary)',
            fontSize: '0.875rem',
            outline: 'none',
            transition: 'all 0.15s ease-in-out'
          }}
          onFocus={(e) => {
            e.target.style.borderColor = 'var(--color-primary)';
            e.target.style.boxShadow = '0 0 0 2px var(--color-primary-light)';
          }}
          onBlur={(e) => {
            e.target.style.borderColor = 'var(--border-medium)';
            e.target.style.boxShadow = 'none';
          }}
          placeholder={t('ENTER_BBCODE_FORMAT_FOR_IMAGES', 'Enter BBCode format for images...')}
        />
      </div>
    </div>
  );
};

export default FormatsTab;
